import React, { useState, useMemo } from "react";
import { useTable, usePagination } from "react-table";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import "./Table.css";

// json
import PositionJSON from "./json/PositionJSON.json";
import ServiceJSON from "./json/ServiceJSON.json";

const UserListTable = ({ userList }) => {
  const [userNameFilter, setUserNameFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [positionFilter, setPositionFilter] = useState("");
  const [serviceFilter, setServiceFilter] = useState(
    ServiceJSON.map((service) => service.key)
  );

  const toggleServiceFilter = (value) => {
    if (serviceFilter.includes(value)) {
      setServiceFilter(serviceFilter.filter((service) => service !== value));
    } else {
      setServiceFilter([...serviceFilter, value]);
    }
  };

  const clearFilters = () => {
    setUserNameFilter("");
    setNameFilter("");
    setPositionFilter("");
    setServiceFilter(ServiceJSON.map((service) => service.key));
  };

  const filteredData = useMemo(
    () =>
      userList.filter(
        (row) =>
          row.username.toLowerCase().includes(userNameFilter.toLowerCase()) &&
          `${row.firstname} ${row.lastname}`
            .toLowerCase()
            .includes(nameFilter.toLowerCase()) &&
          (!positionFilter || row.position === positionFilter) &&
          (row.service.length === 0 ||
            row.service.some((service) => serviceFilter.includes(service.name)))
      ),
    [userList, userNameFilter, nameFilter, positionFilter, serviceFilter]
  );

  const columns = useMemo(
    () => [
      { Header: "ชื่อผู้ใช้งาน", accessor: "username" },
      {
        Header: "ชื่อ - นามสกุล",
        accessor: (row) => (
          <Link to={`/service?username=${row.username}`} className="name-link">
            {row.firstname} {row.lastname}
          </Link>
        ),
      },
      {
        Header: "ตำแหน่ง",
        accessor: (row) =>
          PositionJSON.find((positionMap) => positionMap.key === row.position)
            ?.text || row.position,
      },
      {
        Header: "สถานะการใช้งาน",
        accessor: "active",
        Cell: ({ value }) =>
          value === "INACTIVE" ? (
            <span style={{ color: "red" }}>ปิดการใช้งาน</span>
          ) : (
            <span style={{ color: "green" }}>ใช้งาน</span>
          ),
      },
      {
        Header: "บริการ",
        accessor: (row) =>
          row.service.length === 0
            ? "ไม่มีบริการ"
            : row.service
                .filter((service) => service.active === "ACTIVE")
                .map((service) =>
                  service.name === "e-finance" ? "E-Finance" : "บริการอื่น"
                )
                .join(", "),
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageIndex: 0, pageSize: 10 },
    },
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
  } = tableInstance;

  const history = useNavigate();

  const handleInvitationButtonClick = () => {
    history("/invitation");
  };

  return (
    <div className="table-container">
      <Container className="filters">
        <Row>
          <Col xs={12} sm={12} md={12} lg={3} className="order-2 order-lg-1">
            <h5>
              <u>ตัวเลือกการกรอง</u>
            </h5>
            <Label for="userName">กรองชื่อผู้ใช้งาน</Label>
            <Input
              name="userName"
              type="text"
              value={userNameFilter}
              onChange={(e) => setUserNameFilter(e.target.value)}
              placeholder="กรองชื่อผู้ใช้งาน"
              className="filter-input"
            />
            <br />
            <Label for="name">กรองชื่อ - นามสกุล</Label>
            <Input
              name="name"
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="กรอกชื่อ - นามสกุล"
              className="filter-input"
            />
            <br />
            <Button onClick={() => clearFilters()} className="clear-button">
              ล้างค่าการกรองทั้งหมด
            </Button>
          </Col>

          <Col xs={12} sm={12} md={12} lg={9} className="order-1 order-lg-2">
            <Row className="table-header">
              <Col sm="6" className="table-title">
                <h4>รายชื่อผู้ใช้งาน</h4>
              </Col>
              <Col sm="6" className="text-end">
                <Button
                  onClick={handleInvitationButtonClick}
                  color="primary"
                  className="invitation-button"
                >
                  เชิญผู้ใช้งาน
                </Button>
              </Col>
            </Row>

            <table {...getTableProps()} className="user-list-table">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th {...column.getHeaderProps()}>
                        {column.render("Header")}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="pagination">
              <button
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                ก่อนหน้า
              </button>
              <span>
                หน้า <strong>{state.pageIndex + 1}</strong> จาก{" "}
                {pageOptions.length}
              </span>
              <button onClick={() => nextPage()} disabled={!canNextPage}>
                ถัดไป
              </button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default UserListTable;
